// ============================================================
// index.js — where the app starts: loads the designs, then shows the public site, sign-in, or your workspace.
//
// In this file, in order:
//   Start-up
//   Home page (and the public site frame)
//   Pages: the public site, sign-in, and the workspace
// ============================================================

import React, { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom/client";
import { AuthProvider, LEGAL, PLANS, useAuth } from "./account/AccountContext";
import { AccountGate, CheckoutReturn, PasscodeReveal, PricingPanel } from "./account/AccountScreens";
import { ErrorBoundary } from "./components/ui";
import { NavigationContext, useNavigation, useRouter } from "./context/Navigation";
import { initMonitoring, lazyChunk, readSaved, writeSaved } from "./lib/browser";
import { listenForFix } from "./lib/fix";
import "./styles/app.css";

/* ============================================================
   Start-up
   ============================================================ */

// First, so problems during startup are caught too.
initMonitoring();

// The free checker's "Fix in Gov Social Kit" sends a picture to /fix: catch it
// straight away, before any login, so it's waiting when the workspace opens.
listenForFix();

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <React.StrictMode>
    <ErrorBoundary fullPage>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

/* ============================================================
   Home page
   ============================================================ */

// The public side of the site (logged out): landing, pricing and login.
// None of it loads the editor; that bundle arrives only after login.


/* ------------------------------------------------------------ header */

const Tick = () => <svg className="lp-ck" viewBox="0 0 20 20" aria-hidden="true"><path d="M4.5 10.5l3.4 3.4L15.5 6.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
const scrollToId = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };

function MarketingHeader({ page, signedIn }) {
  const { navigate } = useNavigation();
  // On the home page the links move down the page; elsewhere they open it.
  const section = (id) => (page === "home" ? scrollToId(id) : navigate("home"));
  const link = (label, onClick, active = false) => (
    <button type="button" className={"marketing-header__link" + (active ? " marketing-header__link--active" : "")}
            aria-current={active ? "page" : undefined} onClick={onClick}>{label}</button>
  );
  return (
    <header className="app-header marketing-header">
      <button type="button" className="app-header__brand marketing-header__brand" onClick={() => navigate("home")}>
        <span className="marketing-header__mark" aria-hidden="true"><Tick /></span>
        <span>Gov Social<span className="marketing-header__word"> Kit</span></span>
      </button>
      <nav className="marketing-header__nav" aria-label="Main">
        <span className="marketing-header__links">
          {page === "home" && link("Features", () => section("features"))}
          {page === "home" && link("What you can make", () => section("outputs"))}
          {link("Pricing", () => (page === "home" ? scrollToId("pricing") : navigate("pricing")), page === "pricing")}
          {link("Free alt text checker", () => navigate("alt-text-checker"), page === "alt-text-checker")}
          {link("Help", () => navigate("help"), page === "help")}
        </span>
        {signedIn ? (
          <button type="button" className="lp-btn lp-btn--primary lp-btn--sm" onClick={() => navigate("gallery")}>Open workspace</button>
        ) : (
          <>
            {link("Log in", () => navigate("login", { mode: "login" }), page === "login")}
            <button type="button" className="lp-btn lp-btn--primary lp-btn--sm marketing-header__cta" onClick={() => navigate("login", { mode: "create" })}>
              Get started
            </button>
          </>
        )}
      </nav>
    </header>
  );
}

/* ------------------------------------------------------------ landing */

// The home page. Every claim here is something the app does today: keep it
// that way when you change the words.

const LP_FEATURES = [
  ["Live contrast checker", "Checked as you edit", "Every line of text is measured against what's behind it. Under 4.5:1, it's flagged, with a one-tap fix that keeps your colors as close as possible."],
  ["Alt text, drafted for you", "On every design", "Each design comes with alt text written from its own words, ready for you to review and edit before you post."],
  ["Text size and flashing", "Checked", "Text that's too small, and anything flashing more than three times a second, is flagged before you download."],
  ["Accessibility report", "Every plan", "A printable report for each post, listing every check with its WCAG criterion, plus a log of everything you've downloaded."],
];
const LP_OUTPUTS = [
  ["Social graphics", "Every platform's post, story and cover size, from one design."],
  ["Short video", "Animated posts with captions burned in, plus .vtt and .srt files."],
  ["Web banners and headers", "Banners and page headers for your agency's own website."],
  ["Flyers and posters", "Letter, poster and postcard sizes at 300 dpi for the counter and the bulletin board."],
  ["Adverts", "Newspaper, web banner and social ad sizes, from the same design."],
];
const LP_STEPS = [
  ["Pick a template", "Or paste an announcement: meetings, closures, alerts, events and more."],
  ["Add your content", "Your words, photos, logo and colors. Holidays and dates come ready from the calendar."],
  ["Review the checks", "Contrast, alt text and captions are checked, most with a one-tap fix."],
  ["Download", "Images, video, flyers and ads, sized for every platform."],
];

// Beyond the checks and the formats: the rest of what the app does.
// Real designs from the Toolkit, for a sample county ("Maple County"). To redo them, open
// each template in the app and save the preview at 720 px wide into public/landing/examples.
const LP_EXAMPLES = [
  ["road-closure", "Road closure", "Maple County: Road closed. Main Street, 4th to 6th Street, September 24 to 26, 7 AM to 5 PM. Detour: Oak Street."],
  ["public-notice", "Public notice", "Maple County: Public notice from the Board of Commissioners. Read the full notice on our website."],
  ["election", "Election day", "Maple County: Election day. Polls are open 7 AM to 8 PM. Find your polling place online."],
  ["weather-emergency", "Weather alert", "Maple County: Flood watch. Never drive through a flooded road."],
  ["job-opening", "Job opening", "Maple County: Now hiring. Join the team at Maple County, full-time job with benefits. Apply online."],
  ["community-event", "Community event", "Maple County: Community event. Join us in Maple County for local events."],
  ["board-meeting", "Board meeting", "Maple County: Board meeting on the 24th. View the agenda, date, time and meeting details."],
  ["office-closure", "Office closure", "Maple County: Offices closed Monday, October 12. County offices reopen Tuesday at 8 AM."],
];

const LP_MORE = [
  ["Paste an announcement", "Paste the text and Create builds the whole design: the right template, icon, headline and details."],
  ["A calendar that plans ahead", "Holidays, awareness days, elections and your own events, each with a ready design and a reminder of when to post."],
  ["Made for each department", "The Sheriff, Treasurer, Elections office or Parks each see the templates and posts that fit their work."],
  ["Your brand on every design", "Your colors, fonts and logo throughout, with eight distinct design themes and four layouts to choose from."],
  ["Meetings and slides", "Teams and Zoom backgrounds and presentation slides, made from the same designs."],
  ["Flyers and adverts", "Print flyers, posters and postcards at 300 dpi, plus newspaper, web and social ad sizes, from the same design."],
  ["QR codes", "Codes that open the right page on your website, with the link added to the alt text for people who can't scan."],
  ["Record video", "A recording studio with a teleprompter. Your script becomes the captions."],
  ["Your state's story", "Ready posts with facts, history and quotes from your own state."],
  ["Readable for everyone", "Jargon is flagged with plainer words one tap away, any design can go out in Spanish or another language too, and you can preview it as people with low vision see it."],
  ["Free alt text checker", "One click checks the images on your website and your Facebook, Instagram and X posts, including the automatic alt text those sites write, with a worksheet to fix them."],
  ["Private by design", "Your designs stay on your own computer, with a backup you can download or move any time."],
];

export function Landing() {
  const { navigate } = useNavigation();
  const start = (plan) => navigate("login", { mode: "create", ...(plan ? { plan } : {}) });
  return (
    <div className="lp">
      <section className="lp-hero" aria-labelledby="lp-title">
        <div className="lp-wrap lp-hero__grid">
          <div>
            <h1 id="lp-title">Get your message heard. By everyone.</h1>
            <p className="lp-lede">
              A social media toolkit for county, city, court and school communications teams. Ready-made designs for
              notices, closures, elections, weather alerts and job openings, plus video, flyers and ads from any
              announcement. Contrast, alt text and captions are checked before you post.
            </p>
            <div className="lp-ctas">
              <button type="button" className="lp-btn lp-btn--primary" onClick={() => start()}>Get started</button>
              <button type="button" className="lp-btn lp-btn--ghost" onClick={() => scrollToId("designs")}>See the designs</button>
            </div>
            <p className="lp-trial">Got a trial passcode?{" "}
              <button type="button" className="lp-link" onClick={() => navigate("login", { mode: "login" })}>Log in here</button></p>
            <p className="lp-fine">Runs in any modern browser. No install, no design experience needed.{" "}
              <button type="button" className="lp-link" onClick={() => navigate("alt-text-checker")}>Check your website and Facebook images free</button></p>
          </div>
          <div className="lp-photo">
            <img className="lp-art" src={`${process.env.PUBLIC_URL || ""}/landing/hero-art.svg`} width="635" height="480"
                 alt="A town hall with a megaphone, surrounded by social media platforms and reactions" />
            <div className="lp-panel lp-check" role="img" aria-label="Example of the checks on a design: contrast passes, text size passes, alt text drafted and ready to review">
              <p className="lp-check__head">Accessibility checks <span>Community event</span></p>
              <p className="lp-row lp-row--pass"><Tick /><b>Text contrast</b><em>Pass</em></p>
              <p className="lp-row lp-row--pass"><Tick /><b>Text size</b><em>Pass</em></p>
              <p className="lp-row lp-row--todo"><Tick /><b>Alt text</b><em>Drafted, review it</em></p>
            </div>
          </div>
        </div>
      </section>

      <div className="lp-deadline" role="note">
        <div className="lp-wrap">
          <span>Governments of <strong>50,000+ people</strong> must comply by <strong>April 26, 2027</strong></span>
          <span className="lp-sep" aria-hidden="true">|</span>
          <span>Smaller ones and special districts by <strong>April 26, 2028</strong></span>
        </div>
      </div>

      <section className="lp-section" id="designs" aria-labelledby="lp-designs">
        <div className="lp-wrap">
          <div className="lp-head">
            <h2 className="lp-h2" id="lp-designs">Ready-made designs for what you post every week.</h2>
            <p>Pick one, change the words, and post. Your name, logo and colors go on every design, each one sized for Facebook, Instagram, X, LinkedIn and print, with alt text written for it.</p>
          </div>
          <ul className="lp-examples">
            {LP_EXAMPLES.map(([id, name, alt]) => (
              <li key={id}>
                <img src={`${process.env.PUBLIC_URL || ""}/landing/examples/${id}.jpg`} width="720" height="481" loading="lazy" decoding="async" alt={alt} />
                <span>{name}</span>
              </li>
            ))}
          </ul>
          <p className="lp-note">Plus spotlights, reports, tips, seasonal posts, sales and more, and a calendar of ready posts for holidays and awareness days.</p>
        </div>
      </section>

      <section className="lp-section" id="requirement" aria-labelledby="lp-req">
        <div className="lp-wrap">
          <div className="lp-head">
            <h2 className="lp-h2" id="lp-req">The rule covers more than your website.</h2>
            <p>The Department of Justice's ADA Title II rule asks state and local governments to meet WCAG 2.1 Level AA across their web content, including what they post on social media after their compliance date. Most of what a communications team posts comes down to three things.</p>
          </div>
          <ul className="lp-feat lp-feat--narrow">
            <li><h3>Pictures need alt text</h3><p>Every graphic that carries meaning needs a description a screen reader can read out, not a repeat of the caption.</p></li>
            <li><h3>Video needs accurate captions</h3><p>Captions in sync with what's said. Automatic captions on their own often fall short.</p></li>
            <li><h3>Design choices count</h3><p>Low-contrast text, tiny text and flashing motion are problems, whatever the intent.</p></li>
          </ul>
          <p className="lp-small">A summary to help you plan, not legal advice. Check the rule and your dates with your attorney.</p>
        </div>
      </section>

      <section className="lp-section lp-section--flush" id="features" aria-labelledby="lp-feat">
        <div className="lp-wrap lp-split">
          <div>
            <div className="lp-head lp-head--tight"><h2 className="lp-h2" id="lp-feat">The checks run while you design, so your team doesn't have to remember them.</h2></div>
            <ul className="lp-feat">
              {LP_FEATURES.map(([t, tag, d]) => <li key={t}><h3>{t} <span className="lp-tag">{tag}</span></h3><p>{d}</p></li>)}
            </ul>
          </div>
          <div className="lp-panel lp-editor" role="img" aria-label="Captions on a video: a caption track under the video, burned-in captions, and caption files to download">
            <div className="lp-frame"><span className="lp-cap">Council meets Tuesday at 6 p.m.</span></div>
            <div className="lp-track">
              <div className="lp-lane"><span>Video</span><i className="lp-bar" style={{ flex: 1 }} /></div>
              <div className="lp-lane"><span>Captions</span><i className="lp-bar lp-bar--c" style={{ width: "26%" }} /><i className="lp-bar lp-bar--c" style={{ width: "34%" }} /><i className="lp-bar lp-bar--c" style={{ width: "22%" }} /></div>
            </div>
            <div className="lp-files"><span>Burned-in captions</span><span>captions.vtt</span><span>captions.srt</span></div>
          </div>
        </div>
      </section>

      <section className="lp-section lp-section--tint" id="outputs" aria-labelledby="lp-out">
        <div className="lp-wrap">
          <div className="lp-head">
            <h2 className="lp-h2" id="lp-out">One canvas for social, video, print and web.</h2>
            <p>Your team probably makes these in separate tools today. Here, the same accessibility checks apply to all of them.</p>
          </div>
          <ul className="lp-out">
            {LP_OUTPUTS.map(([t, d]) => <li key={t}><h3>{t}</h3><p>{d}</p></li>)}
          </ul>
          <p className="lp-note">Every design also comes with alt text, post text and hashtag suggestions for each platform; every video with burned-in captions and a caption file.</p>
        </div>
      </section>

      <section className="lp-section" id="more" aria-labelledby="lp-more">
        <div className="lp-wrap">
          <div className="lp-head">
            <h2 className="lp-h2" id="lp-more">Everything your team posts, in one place.</h2>
            <p>Built around how local government communications actually works, from the Sheriff's office to the library.</p>
          </div>
          <ul className="lp-more">
            {LP_MORE.map(([t, d]) => <li key={t}><h3>{t}</h3><p>{d}</p></li>)}
          </ul>
        </div>
      </section>

      <section className="lp-section" aria-labelledby="lp-steps">
        <div className="lp-wrap">
          <div className="lp-head"><h2 className="lp-h2" id="lp-steps">Four steps from announcement to post.</h2></div>
          <ol className="lp-steps">{LP_STEPS.map(([t, d]) => <li key={t}><h3>{t}</h3><p>{d}</p></li>)}</ol>
        </div>
      </section>

      <section className="lp-section lp-section--tint" id="pricing" aria-labelledby="lp-price">
        <div className="lp-wrap">
          <div className="lp-head">
            <h2 className="lp-h2" id="lp-price">One price per year.</h2>
            <p>Every feature in both plans. They differ only in how many departments can use it.</p>
          </div>
          <div className="lp-plans">
            {PLANS.map((plan, i) => (
              <div className={"lp-plan" + (i === 1 ? " lp-plan--hl" : "")} key={plan.id}>
                <h3>{plan.name}</h3>
                <p className="lp-plan__for">{plan.blurb}</p>
                <p className="lp-price">{plan.price}<small>{plan.period}</small></p>
                <ul>{plan.features.map((f) => <li key={f}><Tick />{f}</li>)}</ul>
                <button type="button" className={"lp-btn lp-btn--block " + (i === 1 ? "lp-btn--primary" : "lp-btn--ghost")} onClick={() => start(plan.id)}>
                  Get {plan.name.toLowerCase()}
                </button>
              </div>
            ))}
          </div>
          <div className="lp-buy">
            <p><Tick />Under $500 a year: within many purchasing-card limits, so often no quote or PO needed</p>
            <p><Tick />Pay by card or purchasing card through Stripe, with a receipt for your records</p>
            <p><Tick />Paying by invoice or purchase order, or need a W-9? Email <a className="lp-link" href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a></p>
          </div>
        </div>
      </section>

      <section className="lp-final" aria-labelledby="lp-final">
        <div className="lp-wrap">
          <div>
            <h2 id="lp-final">Your deadline is set. Getting ready for it doesn't have to be hard.</h2>
            <p>Start with the department that posts the most, and add the rest later.</p>
          </div>
          <div className="lp-ctas lp-ctas--end">
            <button type="button" className="lp-btn lp-btn--primary" onClick={() => start()}>Get started</button>
            <button type="button" className="lp-btn lp-btn--dark" onClick={() => navigate("contact")}>Contact us</button>
          </div>
        </div>
      </section>

      <footer className="lp-foot">
        <div className="lp-wrap">
          <p>© {new Date().getFullYear()} {LEGAL.orgName}. Not affiliated with the U.S. Department of Justice or any government agency.</p>
          {LEGAL.contactEmail && <p><a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a></p>}
          <nav aria-label="Site information">
            {[["alt-text-checker", "Alt text checker"], ["help", "Help"], ["about", "About us"], ["contact", "Contact"], ["privacy", "Privacy"], ["cookies", "Cookies"]].map(([id, label]) => (
              <button type="button" key={id} onClick={() => navigate(id)}>{label}</button>
            ))}
          </nav>
        </div>
      </footer>
    </div>
  );
}

/* ------------------------------------------------------------ pricing + login */

export function PublicPricing() {
  const { navigate } = useNavigation();
  return (
    <div className="marketing-page">
      <PricingPanel onChoose={(plan) => navigate("login", { mode: "create", plan })} />
    </div>
  );
}

export function LoginPage({ mode, plan, onPasscode, notice }) {
  return <AccountGate key={`${mode}-${plan}`} embedded initialMode={mode} initialPlan={plan} onPasscode={onPasscode} notice={notice} />;
}

/* ------------------------------------------------------------ shell */

const FOOTER_LINKS = [["alt-text-checker", "Alt text checker"], ["about", "About us"], ["contact", "Contact"], ["help", "Help"], ["privacy", "Privacy"], ["cookies", "Cookies"]];

function MarketingFooter({ page }) {
  const { navigate } = useNavigation();
  return (
    <footer className="marketing-footer">
      <span>© {new Date().getFullYear()} {LEGAL.orgName}</span>
      <nav aria-label="Site information" className="marketing-footer__nav">
        {FOOTER_LINKS.map(([id, label]) => (
          <button type="button" key={id} aria-current={page === id ? "page" : undefined}
                  className={"marketing-footer__link" + (page === id ? " marketing-footer__link--active" : "")}
                  onClick={() => navigate(id)}>{label}</button>
        ))}
      </nav>
    </footer>
  );
}

export function MarketingShell({ page, signedIn = false, children }) {
  // Only the middle scrolls (header and footer are fixed): back to its top on each page.
  // The home page scrolls as one long page, with its own footer at the end.
  const mainRef = React.useRef(null);
  React.useEffect(() => { if (mainRef.current) mainRef.current.scrollTop = 0; }, [page]);
  return (
    <div className={"marketing" + (page === "home" ? " marketing--home" : "")}>
      <MarketingHeader page={page} signedIn={signedIn} />
      <main className="marketing__main" id="main-content" ref={mainRef}>{children}</main>
      {page !== "home" && <MarketingFooter page={page} />}
    </div>
  );
}

/* ============================================================
   Pages: the public site, sign-in, and the workspace
   ============================================================ */

// Shared styles first: every feature stylesheet (imported by its component)
// must load after these, so its rules win where both apply.

// The editor is its own bundle: visitors on the landing and pricing pages
// never download it. It loads right after login.
// Screens that show designs also load designs.json, at the same time as their code.
const withDesigns = (load) => () => Promise.all([load(), import("./lib/designs").then((d) => d.loadDesignsOnce())]).then(([m]) => m);
const Workspace = lazyChunk(withDesigns(() => import("./context/AppContext")), "AppShell");
// Policy text and the help knowledge base also load only when opened. A
// design opened from a link shows with the Toolkit's code.
const InfoContent = lazyChunk(() => import("./components/Site"), "InfoContent");
const PublicHelp = lazyChunk(() => import("./components/Site"), "PublicHelp");
const SharedDesign = lazyChunk(withDesigns(() => import("./components/Gallery")), "SharedDesign");

// Info pages are public and look the same whether or not you're logged in.
const INFO_PAGES = ["about", "contact", "privacy", "cookies", "design", "alt-text-checker"];
// checkout: Stripe sends people back here, signed in or not (see account/AccountScreens.js).
const PUBLIC_PAGES = ["home", "pricing", "login", "help", "checkout", ...INFO_PAGES];
// help also exists inside the workspace. Pricing is public only: signed-in
// users see their plan (and upgrade) in Settings instead.
const APP_PAGES = ["gallery", "create", "fix", "themes", "designs", "favorites", "calendar", "social", "caption", "help", "settings"];
// Old addresses that now live elsewhere.
const MOVED = { downloads: "social" };
const ALL_PAGES = [...new Set([...PUBLIC_PAGES, ...APP_PAGES, ...Object.keys(MOVED)])];
const PAGE_TITLES = {
  home: "Accessible social media for local government", login: "Log in", gallery: "Toolkit", create: "Create", fix: "Fix an image", themes: "Themes", designs: "Toolkit", favorites: "Toolkit", calendar: "Calendar", social: "Publish",
  caption: "Record", downloads: "Downloads", pricing: "Pricing", settings: "Settings", checkout: "Checkout",
  help: "Help", "alt-text-checker": "Free alt text checker", about: "About us", contact: "Contact us", privacy: "Privacy Policy", cookies: "Cookie Policy", design: "Shared design",
};
const RETURN_KEY = "gsk-return-to";

// What search engines show for each public page (they run the app, unlike
// link-preview bots, which read public/index.html).
const PAGE_DESCRIPTIONS = {
  home: "Accessible social posts, videos, flyers and ads for local government, with contrast, alt text and captions checked as you design. Built for counties, cities, courts and school districts.",
  pricing: "One department $149 a year, all departments $499 a year. Every feature included: templates, videos, flyers, accessibility checks and reports.",
  help: "Answers about designing, sizing, captions, accessibility checks, QR codes, fonts and backups in Gov Social Kit.",
  about: "Gov Social Kit helps local governments post accessible, on-brand updates in minutes.",
  contact: "Contact the Gov Social Kit team.",
  "alt-text-checker": "Free image and alt text checker for government websites. Find missing and poor alt text in seconds, right in your browser.",
  privacy: "How Gov Social Kit handles your information: your work stays in your browser.",
  cookies: "The browser storage Gov Social Kit uses, and why.",
};

function useDocumentTitle(title, page) {
  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && PAGE_DESCRIPTIONS[page]) meta.setAttribute("content", PAGE_DESCRIPTIONS[page]);
  }, [title, page]);
}

const remember = (path) => writeSaved(RETURN_KEY, path, { session: true });
const recall = () => { const path = readSaved(RETURN_KEY, { session: true }); writeSaved(RETURN_KEY, null, { session: true }); return path; };

function Loading({ label = "Loading…" }) {
  return <main className="gate"><p className="contrast-note" role="status">{label}</p></main>;
}

function Root() {
  const { session, ready, logout } = useAuth();
  const [page, routerNavigate] = useRouter(ALL_PAGES, "home");
  const [gateMode, setGateMode] = useState("login");
  const [gatePlan, setGatePlan] = useState("department");
  const [notice, setNotice] = useState("");
  // A new passcode (bought, renewed, upgraded) is shown before anything else,
  // so it can't be missed: { code, details }.
  const [newCode, setNewCode] = useState(null);
  const showPasscode = useCallback((code, details) => setNewCode({ code, details }), []);

  // navigate("login", { mode: "create", plan: "all" }) also picks the form's tab and plan.
  const navigate = useCallback((next, opts = {}) => {
    if (opts.mode) setGateMode(opts.mode);
    if (opts.plan) setGatePlan(opts.plan);
    if (next !== "login") setNotice("");
    routerNavigate(next, opts);
  }, [routerNavigate]);

  useDocumentTitle(`${PAGE_TITLES[page] || "Home"} · Gov Social Kit`, page);

  // Logged out on a tool page: remember where they were going, send them to log in.
  // Logged in on home/login: go to the workspace (or the page they asked for).
  useEffect(() => {
    if (!ready || newCode) return;
    const isToolPage = (APP_PAGES.includes(page) || MOVED[page]) && !PUBLIC_PAGES.includes(page);
    if (!session && isToolPage) {
      // Keep the query string too: Stripe returns to /checkout?session_id=…
      remember(window.location.pathname + window.location.search);
      setGateMode("login");
      setNotice("Log in to use the template tools.");
      routerNavigate("login", { replace: true });
    } else if (session && MOVED[page]) {
      routerNavigate(MOVED[page], { replace: true });
    } else if (session && page === "pricing") {
      routerNavigate("settings", { replace: true });
    } else if (session && (page === "home" || page === "login")) {
      const back = recall();
      const target = back && back !== "/" && !back.startsWith("/login") ? back : "/gallery";
      // "/checkout?session_id=…" -> page "checkout"; the full target (with its
      // query string) is kept as the URL.
      const targetPage = target.split(/[?#]/)[0].split("/")[1] || "gallery";
      routerNavigate(targetPage, { replace: true, path: target });
    }
  }, [ready, session, page, newCode, routerNavigate]);

  const signOut = useCallback(() => {
    logout();
    navigate("home");
  }, [logout, navigate]);

  const nav = useMemo(() => ({ page, navigate }), [page, navigate]);
  return <NavigationContext.Provider value={nav}>{renderPage()}</NavigationContext.Provider>;

  function renderPage() {
  if (!ready) return <Loading />;

  if (newCode) {
    return (
      <PasscodeReveal name={session ? session.name : ""} code={newCode.code}
                      details={{ ...newCode.details, plan: session && session.plan, expiresAt: session && session.expiresAt }}
                      onDone={() => { setNewCode(null); routerNavigate("gallery", { replace: true }); }} />
    );
  }

  if (page === "checkout") return <CheckoutReturn onPasscode={showPasscode} />;

  if (!session) {
    const publicPage = PUBLIC_PAGES.includes(page) ? page : "login";
    return (
      <MarketingShell page={publicPage}>
        {publicPage === "home" && <Landing />}
        {publicPage === "pricing" && <PublicPricing />}
        {publicPage === "login" && <LoginPage mode={gateMode} plan={gatePlan} onPasscode={showPasscode} notice={notice} />}
        <Suspense fallback={<p className="contrast-note marketing-page" role="status">Loading…</p>}>
          {publicPage === "help" && <PublicHelp />}
          {INFO_PAGES.includes(publicPage) && <div className={"marketing-page" + (publicPage === "alt-text-checker" ? " marketing-page--wide" : "")}>{publicPage === "design" ? <SharedDesign signedIn={false} /> : <InfoContent page={publicPage} />}</div>}
        </Suspense>
      </MarketingShell>
    );
  }

  // Signed in, reading About / Privacy / Cookies: same public page, with an "Open workspace" button.
  if (INFO_PAGES.includes(page)) {
    return (
      <MarketingShell page={page} signedIn>
        <Suspense fallback={<p className="contrast-note marketing-page" role="status">Loading…</p>}>
          <div className={"marketing-page" + (page === "alt-text-checker" ? " marketing-page--wide" : "")}>{page === "design" ? <SharedDesign signedIn /> : <InfoContent page={page} />}</div>
        </Suspense>
      </MarketingShell>
    );
  }

  // Signed in; home/login are being redirected by the effect above.
  if (page === "home" || page === "login" || page === "pricing" || MOVED[page]) return <Loading label="Opening your workspace…" />;

  return (
    <Suspense fallback={<Loading label="Opening your workspace…" />}>
      <Workspace page={page} session={session} onSignOut={signOut} onPasscode={showPasscode} />
    </Suspense>
  );
  }
}

export function App() {
  return (
    <AuthProvider>
      <Root />
    </AuthProvider>
  );
}
